import React, { useState, useEffect, useCallback, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { CheckSquare, Square, ExternalLink, Clock, FileText, ThumbsUp, ThumbsDown, Upload, Paperclip, Trash2, Loader2, File as FileIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STATUSES } from "@/lib/ui";
import { StatusBadge, PriorityBadge, ApprovalBadge, ProgressBar, SectionLabel } from "@/components/Bits";
import AuthImage from "@/components/AuthImage";

const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-white focus:ring-1 focus:ring-white transition-colors";

function Meta({ label, value }) {
  return (
    <div className="border border-white/10 bg-[#0a0a0a] rounded-sm px-3 py-2">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-200 mt-0.5 break-words">{value || "—"}</div>
    </div>
  );
}

export default function TaskDrawer({ taskId, open, onOpenChange, onChanged }) {
  const [task, setTask] = useState(null);
  const [upd, setUpd] = useState({ status: "", progress: "", work_done: "", proof_url: "", next_action: "" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!taskId) return;
    const { data } = await api.get(`/tasks/${taskId}`);
    setTask(data);
    setUpd({ status: data.status, progress: data.progress, work_done: "", proof_url: "", next_action: "" });
  }, [taskId]);

  useEffect(() => {
    if (open && taskId) load();
  }, [open, taskId, load]);

  const saveUpdate = async () => {
    try {
      await api.post(`/tasks/${taskId}/update`, {
        status: upd.status,
        progress: upd.progress === "" ? null : Number(upd.progress),
        work_done: upd.work_done,
        proof_url: upd.proof_url,
        next_action: upd.next_action,
      });
      toast.success("Update saved");
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleCheck = async (index) => {
    await api.post(`/tasks/${taskId}/checklist?index=${index}`);
    await load();
    onChanged && onChanged();
  };

  const decide = async (decision) => {
    await api.post(`/approvals/${taskId}?decision=${encodeURIComponent(decision)}`);
    toast.success(`Approval ${decision}`);
    await load();
    onChanged && onChanged();
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tasks/${taskId}/files`, fd);
      toast.success("File uploaded");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteFile = async (fileId) => {
    await api.delete(`/tasks/${taskId}/files/${fileId}`);
    await load();
    onChanged && onChanged();
  };

  const isImage = (ct) => (ct || "").startsWith("image/");

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-[#0d0d0d] border-l border-white/10 text-white w-full sm:max-w-xl overflow-y-auto p-0">
        {task && (
          <div data-testid="task-drawer">
            <SheetHeader className="px-6 py-5 border-b border-white/10 bg-[#121212] sticky top-0 z-10">
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                <span>{task.ref || task.id?.slice(-4)}</span>
                <span className="text-zinc-700">·</span>
                <span>{task.division}</span>
              </div>
              <SheetTitle className="font-display text-xl text-white text-left">{task.title}</SheetTitle>
              <SheetDescription className="sr-only">Task details, checklist, quick update and activity history.</SheetDescription>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <StatusBadge status={task.status} />
                <PriorityBadge priority={task.priority} />
                <ApprovalBadge status={task.approval_status} />
              </div>
            </SheetHeader>

            <div className="p-6 space-y-6">
              {/* progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <SectionLabel>Progress</SectionLabel>
                  <span className="font-display font-bold text-lg">{task.progress}%</span>
                </div>
                <ProgressBar value={task.progress} />
              </div>

              {/* meta grid */}
              <div className="grid grid-cols-2 gap-2">
                <Meta label="Brand" value={task.brand} />
                <Meta label="Assigned To" value={task.assigned_to} />
                <Meta label="Approver" value={task.approver} />
                <Meta label="Due Date" value={task.due_date} />
                {task.platform && <Meta label="Platform" value={task.platform} />}
                {task.work_type && <Meta label="Work Type" value={task.work_type} />}
                {task.sku_count != null && <Meta label="Products / SKUs" value={task.sku_count} />}
                {task.shoot_date && <Meta label="Shoot Date" value={task.shoot_date} />}
                {task.crew && <Meta label="Crew" value={task.crew} />}
                {task.domain && <Meta label="Domain" value={task.domain} />}
              </div>

              {task.description && (
                <div>
                  <SectionLabel>Scope</SectionLabel>
                  <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{task.description}</p>
                </div>
              )}

              {/* files */}
              <div className="grid grid-cols-1 gap-2">
                {task.working_url && (
                  <a href={task.working_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#4DA3FF] hover:underline border border-white/10 bg-[#0a0a0a] rounded-sm px-3 py-2">
                    <FileText size={15} /> Working folder <ExternalLink size={13} />
                  </a>
                )}
                {task.final_url && (
                  <a href={task.final_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#00FF66] hover:underline border border-white/10 bg-[#0a0a0a] rounded-sm px-3 py-2">
                    <ExternalLink size={15} /> Final URL / Proof
                  </a>
                )}
              </div>

              {/* approval actions */}
              {["Sent", "Revision Requested"].includes(task.approval_status) && (
                <div className="flex gap-2">
                  <button data-testid="drawer-approve" onClick={() => decide("Approved")} className="flex-1 flex items-center justify-center gap-2 text-sm bg-[#00FF66]/10 border border-[#00FF66]/30 text-[#00FF66] hover:bg-[#00FF66]/20 py-2 rounded-sm transition-colors">
                    <ThumbsUp size={15} /> Approve
                  </button>
                  <button data-testid="drawer-revise" onClick={() => decide("Revision Requested")} className="flex-1 flex items-center justify-center gap-2 text-sm bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF6259] hover:bg-[#FF3B30]/20 py-2 rounded-sm transition-colors">
                    <ThumbsDown size={15} /> Request Revision
                  </button>
                </div>
              )}

              {/* files & proof */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <SectionLabel>Files & Proof of Work</SectionLabel>
                  <button data-testid="upload-file-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider border border-white/15 text-zinc-300 hover:bg-white/5 px-2.5 py-1 rounded-sm transition-colors disabled:opacity-60">
                    {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
                  </button>
                  <input ref={fileRef} data-testid="file-input" type="file" className="hidden" onChange={uploadFile} />
                </div>
                {(task.files || []).length === 0 ? (
                  <div className="border border-dashed border-white/10 rounded-sm p-4 text-center text-xs text-zinc-600 das-grid">
                    Attach shoot files, final images or documents as proof of work.
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {task.files.map((f) => (
                      <div key={f.id} data-testid={`file-${f.id}`} className="border border-white/10 bg-[#0a0a0a] rounded-sm overflow-hidden group relative">
                        {isImage(f.content_type) ? (
                          <AuthImage path={f.storage_path} className="w-full h-24 object-cover" />
                        ) : (
                          <div className="w-full h-24 flex items-center justify-center text-zinc-600"><FileIcon size={26} /></div>
                        )}
                        <div className="flex items-center gap-1.5 px-2 py-1.5">
                          <Paperclip size={11} className="text-zinc-600 shrink-0" />
                          <span className="text-[11px] text-zinc-400 truncate flex-1">{f.filename}</span>
                          <button data-testid={`delete-file-${f.id}`} onClick={() => deleteFile(f.id)} className="text-zinc-600 hover:text-[#FF6259] opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={13} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* checklist */}
              <div>
                <SectionLabel>Completion Checklist</SectionLabel>
                <div className="mt-2 space-y-1">
                  {(task.checklist || []).map((c, i) => (
                    <button key={i} data-testid={`checklist-item-${i}`} onClick={() => toggleCheck(i)} className="w-full flex items-center gap-2.5 text-left text-sm py-1.5 px-2 rounded-sm hover:bg-white/5 transition-colors">
                      {c.done ? <CheckSquare size={16} className="text-[#00FF66]" /> : <Square size={16} className="text-zinc-600" />}
                      <span className={c.done ? "text-zinc-500 line-through" : "text-zinc-300"}>{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* quick update */}
              <div className="border border-white/10 bg-[#121212] rounded-sm p-4 space-y-3">
                <SectionLabel>Quick Update</SectionLabel>
                <div className="grid grid-cols-2 gap-3">
                  <select data-testid="drawer-status-select" className={inputCls} value={upd.status} onChange={(e) => setUpd((u) => ({ ...u, status: e.target.value }))}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input data-testid="drawer-progress-input" type="number" className={inputCls} placeholder="Progress %" value={upd.progress} onChange={(e) => setUpd((u) => ({ ...u, progress: e.target.value }))} />
                </div>
                <textarea data-testid="drawer-workdone-input" className={inputCls} rows={2} placeholder="Work done / update" value={upd.work_done} onChange={(e) => setUpd((u) => ({ ...u, work_done: e.target.value }))} />
                <input className={inputCls} placeholder="Proof / Work URL" value={upd.proof_url} onChange={(e) => setUpd((u) => ({ ...u, proof_url: e.target.value }))} />
                <input className={inputCls} placeholder="Next action" value={upd.next_action} onChange={(e) => setUpd((u) => ({ ...u, next_action: e.target.value }))} />
                <button data-testid="drawer-save-update" onClick={saveUpdate} className="w-full text-sm bg-white text-black font-medium py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
                  Save Update
                </button>
              </div>

              {/* activity */}
              <div>
                <SectionLabel>Activity History</SectionLabel>
                <div className="mt-3 space-y-3">
                  {(task.activity || []).map((a, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="w-2 h-2 rounded-full bg-white mt-1.5" />
                        {i < task.activity.length - 1 && <div className="w-px flex-1 bg-white/10 my-1" />}
                      </div>
                      <div className="pb-2">
                        <div className="text-sm text-zinc-200 font-medium">{a.action}</div>
                        {a.note && <div className="text-xs text-zinc-400 mt-0.5">{a.note}</div>}
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-1 flex items-center gap-1">
                          <Clock size={11} /> {a.by} · {(a.at || "").slice(0, 10)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
